import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getConferences} from 'src/openapi/admin/apis/ConferenceApi';
import {useAppDispatch} from 'src/store/hooks';

import {SELECTED_COLUMNS} from '../constants';
import {
    userConferencesConferenceActions,
    userConferencesConferenceDataSelector,
    userConferencesConferenceResultSelector,
} from '../slices/user-conferences-conference.slice';
import {userConferencesStateSelector} from '../slices/user-conferences-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserConferencesCacheKeys} from '../typedef';
import type {Conference} from 'src/openapi/admin/models/Conference';


type Props = {
    accountIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserConferencesConference = ({accountIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(() => apiDispatch(getConferences({
        requestBody: [...new Set(accountIds)],
    })), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, Conference>,
        dataCacheKeys: UserConferencesCacheKeys,
    ) => {
        dispatch(userConferencesConferenceActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(() => (
        checkShouldLoadColumn(columnId, SELECTED_COLUMNS)
    ), [columnId]);

    return useCachedTable({
        dataSelector: userConferencesConferenceDataSelector,
        stateSelector: userConferencesStateSelector,
        resultSelector: userConferencesConferenceResultSelector,
        actions: userConferencesConferenceActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
